import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '../../components/admin/AdminNav';
import EmailReplyModal from '../../components/admin/EmailReplyModal';
import CurationPipelineCard from '../../components/admin/CurationPipelineCard';

const EmailDigestManagement = ({ backendUrl, currentUser }) => {
  const navigate = useNavigate();
  const [previewData, setPreviewData] = useState(null);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoursBack, setHoursBack] = useState(24);
  const [aiTestCount, setAiTestCount] = useState(0);
  const [testEmail, setTestEmail] = useState('');
  const [scheduleHour, setScheduleHour] = useState(8);
  const [scheduleMinute, setScheduleMinute] = useState(30);
  const [scheduleType, setScheduleType] = useState('daily'); // daily, once, disabled
  const [scheduleDate, setScheduleDate] = useState('');
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [regionalOfficers, setRegionalOfficers] = useState([]);
  const [loadingOfficers, setLoadingOfficers] = useState(true);
  const [sendGridConnected, setSendGridConnected] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [emailJobs, setEmailJobs] = useState(null);
  const [loadingEmailJobs, setLoadingEmailJobs] = useState(false);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [templatePreviewTitle, setTemplatePreviewTitle] = useState('');
  const [templatePreviewHtml, setTemplatePreviewHtml] = useState('');

  // Email Template Studio (all emails)
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('weekly_summary');
  const [templateForm, setTemplateForm] = useState({
    subject_template: '',
    header_title: '',
    header_subtitle: '',
    intro_text: '',
    footer_text: ''
  });
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Inbound inbox (admin view)
  const [inboundEmails, setInboundEmails] = useState([]);
  const [loadingInboundEmails, setLoadingInboundEmails] = useState(false);
  const [inboundFilter, setInboundFilter] = useState({ status: 'all', mapped: 'all', responded: 'all' });

  // UI: collapse help sections
  const [showRoutingHelp, setShowRoutingHelp] = useState(false);
  
  // Email Reply Modal
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [selectedEmailForReply, setSelectedEmailForReply] = useState(null);

  // Track AdminNav mode
  const [navMode, setNavMode] = React.useState(() => {
    return localStorage.getItem('adminNavMode') || (window.innerWidth < 1024 ? 'peek' : 'expanded');
  });

  // Listen for AdminNav toggle events
  React.useEffect(() => {
    const handleNavToggle = (e) => {
      if (e.detail.mode) {
        setNavMode(e.detail.mode);
      } else {
        setNavMode(e.detail.isCollapsed ? 'collapsed' : 'expanded');
      }
    };
    window.addEventListener('adminNavToggle', handleNavToggle);
    return () => window.removeEventListener('adminNavToggle', handleNavToggle);
  }, []);

  // Handle clicks inside the preview iframe (open submission in-app, same tab)
  React.useEffect(() => {
    const handler = (event) => {
      const data = event?.data || {};
      if (data?.type === 'tagquest_open_submission' && data?.inspectionId) {
        setShowTemplatePreview(false);
        // Open the admin "Edit Tag Lead" modal via approvals page deep link
        navigate(`/admin/approvals?status=all&openInspectionId=${encodeURIComponent(data.inspectionId)}&returnTo=${encodeURIComponent('/admin/emails')}`);
      } else if (data?.type === 'tagquest_preview_navigate' && data?.href) {
        // Best-effort navigation for other CTA links in preview
        const href = String(data.href || '');
        setShowTemplatePreview(false);
        try {
          const origin = window.location.origin;
          if (href.startsWith(origin)) {
            const path = href.replace(origin, '') || '/';
            navigate(path);
            return;
          }
          if (href.startsWith('/')) {
            navigate(href);
            return;
          }
          // external: open new tab
          window.open(href, '_blank', 'noopener,noreferrer');
        } catch (e) {
          // ignore
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [navigate]);

  // Load scheduler status, regional officers, and email history on mount
  React.useEffect(() => {
    loadSchedulerStatus();
    loadRegionalOfficers();
    loadEmailHistory();
    loadEmailJobsStatus();
    loadTemplateList();
    loadTemplate(selectedTemplateId);
    loadInboundEmails();
  }, [backendUrl]);

  React.useEffect(() => {
    loadTemplate(selectedTemplateId);
  }, [selectedTemplateId]);

  const loadTemplateList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/email-templates`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data?.success) setTemplates(data.templates || []);
    } catch (err) {
      // non-fatal
    }
  };

  const loadTemplate = async (templateId) => {
    if (!templateId) return;
    setLoadingTemplate(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/email-templates/${templateId}`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data?.success && data.template) {
        setTemplateForm({
          subject_template: data.template.subject_template || '',
          header_title: data.template.header_title || '',
          header_subtitle: data.template.header_subtitle || '',
          intro_text: data.template.intro_text || '',
          footer_text: data.template.footer_text || ''
        });
      }
    } catch (err) {
      // non-fatal
    } finally {
      setLoadingTemplate(false);
    }
  };

  const saveTemplate = async () => {
    setSavingTemplate(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/email-templates/${selectedTemplateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(templateForm)
      });
      const data = await response.json();
      if (data?.success) {
        await loadTemplateList();
        alert('✅ Template saved');
      } else {
        alert(`❌ Save failed: ${data?.detail || data?.error || 'unknown error'}`);
      }
    } catch (err) {
      alert(`❌ Save failed: ${err.message}`);
    } finally {
      setSavingTemplate(false);
    }
  };

  const previewSelectedTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/email-templates/${selectedTemplateId}/preview-html`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const html = await response.text();
      setTemplatePreviewTitle(`Template Preview: ${selectedTemplateId}`);
      setTemplatePreviewHtml(html);
      setShowTemplatePreview(true);
    } catch (err) {
      alert(`❌ Failed to load template preview: ${err.message}`);
    }
  };

  const loadInboundEmails = async (filterOverride = null) => {
    setLoadingInboundEmails(true);
    try {
      const f = filterOverride || inboundFilter;
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (f.status && f.status !== 'all') params.set('status', f.status);
      if (f.mapped && f.mapped !== 'all') params.set('mapped', f.mapped === 'mapped' ? 'true' : 'false');
      if (f.responded && f.responded !== 'all') params.set('responded', f.responded === 'responded' ? 'true' : 'false');

      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/inbound-emails?${params.toString()}`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data?.success) setInboundEmails(data.emails || []);
    } catch (err) {
      // non-fatal
    } finally {
      setLoadingInboundEmails(false);
    }
  };

  const takeReplyAction = async ({ inspectionId, inboundEmailId, action, notes }) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/admin/inspections/${inspectionId}/reply-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          action,
          notes: notes || '',
          inbound_email_id: inboundEmailId || null
        })
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.detail || data?.error || 'Failed');
      await loadInboundEmails();
      alert('✅ Action saved');
    } catch (e) {
      alert(`❌ Action failed: ${e.message}`);
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/scheduler-status`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      if (data.success && data.scheduler_running) {
        setSchedulerStatus(data);

        // Extract current schedule from jobs
        if (data.jobs) {
          const dailyJob = data.jobs.find(j => j.id === 'daily_digest');
          if (dailyJob && dailyJob.trigger) {
            const match = dailyJob.trigger.match(/hour='(\d+)'.*minute='(\d+)'/);
            if (match) {
              setScheduleHour(parseInt(match[1]));
              setScheduleMinute(parseInt(match[2]));
            }
          }
        }

        // Test SendGrid connection
        try {
          const sgToken = localStorage.getItem('token');
          const sgResponse = await fetch(`${backendUrl}/api/admin/test-sendgrid`, {
            headers: { 
              'X-Local-Dev': 'true',
              ...(sgToken && { 'Authorization': `Bearer ${sgToken}` })
            }
          });
          const sgData = await sgResponse.json();
          setSendGridConnected(sgData.success === true);
        } catch (e) {
          setSendGridConnected(false);
        }
      } else {
        setSchedulerStatus(null);
      }
    } catch (err) {
      console.error('Failed to load scheduler status:', err);
      setSchedulerStatus(null);
    }
  };

  const loadRegionalOfficers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/regional-officers`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data.success) {
        setRegionalOfficers(data.officers || []);
      }
    } catch (err) {
      console.error('Failed to load regional officers:', err);
    } finally {
      setLoadingOfficers(false);
    }
  };

  const loadEmailHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/email-history`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data.success) {
        setEmailHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to load email history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadEmailJobsStatus = async () => {
    setLoadingEmailJobs(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/email-jobs-status`, {
        headers: { 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data.success) {
        setEmailJobs(data);
      } else {
        setEmailJobs(null);
      }
    } catch (err) {
      console.error('Failed to load email jobs status:', err);
      setEmailJobs(null);
    } finally {
      setLoadingEmailJobs(false);
    }
  };

  const toggleAllEmailJobs = async (enabled) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/email-jobs/toggle-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ enabled })
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to toggle all email jobs');
      setEmailJobs(data);
    } catch (err) {
      alert(`❌ ${err.message}`);
    }
  };

  const toggleEmailJob = async (jobId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/email-jobs/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ job_id: jobId, enabled })
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to toggle email job');
      
      // ✅ Optimistic UI update - update just this job in state immediately
      if (emailJobs && emailJobs.jobs) {
        const updatedJobs = emailJobs.jobs.map(job => 
          job.id === jobId ? { ...job, paused: !enabled } : job
        );
        const allEnabled = updatedJobs.every(j => j.exists && j.paused === false);
        setEmailJobs({ ...emailJobs, jobs: updatedJobs, all_enabled: allEnabled });
      }
      
      // Then reload for confirmation (in background)
      setTimeout(() => loadEmailJobsStatus(), 500);
    } catch (err) {
      alert(`❌ ${err.message}`);
      // Reload on error to reset state
      loadEmailJobsStatus();
    }
  };

  // NOTE: We use the unified Template Studio preview for all email previews.

  const handleUpdateSchedule = async () => {
    try {
      const payload = {
        hour: scheduleHour,
        minute: scheduleMinute,
        type: scheduleType
      };

      if (scheduleType === 'once' && scheduleDate) {
        payload.date = scheduleDate;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/update-schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        alert(`✅ ${data.message}`);
        loadSchedulerStatus();
      } else {
        throw new Error(data.error || 'Failed to update schedule');
      }
    } catch (err) {
      alert(`❌ Error: ${err.message}`);
    }
  };

  const handlePreview = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/preview-daily-leads?hours_back=${hoursBack}`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to fetch preview');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch preview');
      }

      // Transform backend data to match our preview format
      const totalInspections = data.total_leads || 0;
      const leadsByBranch = data.leads_by_branch || {};
      const branches = Object.keys(leadsByBranch);
      const unassigned = leadsByBranch['Unassigned'] || [];

      // Extract unique business names
      const allBusinesses = new Set();
      Object.values(leadsByBranch).forEach(leads => {
        leads.forEach(lead => {
          if (lead.business_name) {
            allBusinesses.add(lead.business_name);
          }
        });
      });

      setPreviewData({
        subject: `TagQuest Daily Leads - ${totalInspections} New Opportunities - ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
        recipients: [
          'matt.hales@pyebarkerfs.com',
          'aidevteam@pyebarkerfs.com',
          'shiva.mogili@pyebarkerfs.com',
          'mohith.manchuru@pyebarkerfs.com'
        ],
        inspectionCount: totalInspections,
        pendingCount: unassigned.length,
        newBusinesses: Array.from(allBusinesses),
        leadsByBranch: leadsByBranch,
        branches: branches.filter(b => b !== 'Unassigned')
      });
    } catch (err) {
      console.error('Preview error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendDigest = async () => {
    setSending(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/send-daily-digest`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send digest');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to send digest');
      }

      alert(`✅ Email digest sent successfully!\n\n${data.leads || 0} leads reported to ${data.branches || 0} branches`);

      // Refresh preview after sending
      if (previewData) {
        handlePreview();
      }
    } catch (err) {
      console.error('Send error:', err);
      setError(err.message);
      alert(`❌ Failed to send digest: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleAITestEmail = async () => {
    setSending(true);
    setError(null);

    try {
      const userEmail = currentUser?.email || 'admin@pyebarkerfs.com';
      const userName = currentUser?.name || 'Admin';

      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/test-email`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          to_email: 'matt@hales.ai',  // Always send to Matt
          user_email: userEmail,  // Who clicked (for conversation)
          user_name: userName
        })
      });
      const data = await response.json();

      if (data.success) {
        setAiTestCount(data.test_count || 1);
        alert(`🤖 AI-Powered Test Email Sent!\\n\\nTest #${data.test_count}\\nCheck matt@hales.ai inbox!\\n\\nEach test gets a unique AI-generated message that builds on previous conversations! 😂`);
      } else {
        alert(`❌ Failed: ${data.error}`);
      }
    } catch (err) {
      console.error('AI test error:', err);
      alert(`❌ Error: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleSendHotLeadDigest = async () => {
    if (!window.confirm('🔥 Send hot lead digest for DUE inspections?\\n\\nThis will email all branches with inspections that are due or past due.')) {
      return;
    }

    setSending(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/send-hot-lead-digest`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send hot lead digest');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to send hot lead digest');
      }

      let message = `✅ Hot lead digest sent successfully!\n\n` +
        `• ${data.leads_sent || 0} leads sent to ${data.branches || 0} branches\n` +
        `• ${data.unassigned || 0} unassigned leads\n` +
        `• Mode: ${data.mode || 'test'}\n`;

      // Add debug info if available
      if (data.debug_info) {
        message += `\n📊 Debug:\n• Total due: ${data.debug_info.total_due_leads}\n• Branches: ${data.debug_info.branches_found?.join(', ')}\n`;
      }

      // Add failed branches with error details
      if (data.failed_branches && data.failed_branches.length > 0) {
        message += `\n⚠️ Failed branches: ${data.failed_branches.join(', ')}`;

        // Show error details if available
        if (data.error_details) {
          message += '\n\nError Details:';
          for (const [branch, error] of Object.entries(data.error_details)) {
            message += `\n• ${branch}: ${error}`;
          }
        }
      }

      alert(message);

      // Refresh email history to show new send
      setTimeout(() => loadEmailHistory(), 2000);
    } catch (err) {
      console.error('Hot lead send error:', err);
      setError(err.message);
      alert(`❌ Failed to send hot lead digest: ${err.message}`);
      // Still refresh history to show failed attempt
      setTimeout(() => loadEmailHistory(), 2000);
    } finally {
      setSending(false);
    }
  };

  const handleSendAITestEmail = async () => {
    setSending(true);
    setError(null);

    try {
      const userEmail = currentUser?.email || 'matt.hales@pyebarkerfs.com';
      const userName = currentUser?.name || 'Matt';
      const targetEmail = testEmail || 'matt@hales.ai';

      const token2 = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/test-email`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token2 && { 'Authorization': `Bearer ${token2}` })
        },
        body: JSON.stringify({
          to_email: targetEmail,
          user_name: userName,
          user_email: userEmail
        })
      });
      const data = await response.json();

      if (data.success) {
        alert(`🤖 AI-POWERED TEST EMAIL SENT!\n\n📧 Sent to: ${data.sent_to}\n🎯 Test #${data.test_count}\n\n${data.ai_message_preview}\n\nCheck your inbox! Claude is hilarious! 😂`);
      } else {
        alert(`❌ Failed: ${data.error}\n\n(But here's what Claude would've said:\n${data.ai_message})`);
      }
    } catch (err) {
      console.error('AI test email error:', err);
      alert(`❌ Error: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  const getNavOffset = () => {
    switch (navMode) {
      case 'peek': return 'ml-4 w-[calc(100%-1rem)]';
      case 'collapsed': return 'ml-20 w-[calc(100%-5rem)]';
      default: return 'ml-64 w-[calc(100%-16rem)]';
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-[#00314F] via-[#003d5c] to-[#00314F] text-white ${getNavOffset()} overflow-x-hidden`}>
      <AdminNav />
      <div className="pt-6 px-4 max-w-full">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">Email Digest Management</h1>
          <p className="text-gray-400 text-xs sm:text-sm">Preview and send daily lead digests</p>
        </div>
        
        {/* Link to New Gmail Inbox */}
        <div className="mb-4 p-3 bg-gradient-to-r from-[#0DB14B]/10 to-[#235EAC]/10 border border-[#0DB14B]/30 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">✉️</span>
            <div>
              <div className="font-semibold text-white">New Gmail-Style Inbox Available</div>
              <div className="text-xs text-gray-400">Clean inbox view, threaded conversations, automation dashboard</div>
            </div>
          </div>
          <a 
            href="/admin/inbox" 
            className="px-4 py-2 bg-[#0DB14B] hover:bg-[#0DB14B]/80 text-white rounded-lg text-sm font-semibold transition-colors"
          >
            Open Inbox →
          </a>
        </div>

        {/* Lead Curation Pipeline */}
        <div className="mb-6">
          <CurationPipelineCard backendUrl={backendUrl} />
        </div>

        {/* User Email Notifications */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold mb-1">User Email Notifications</h2>
              <p className="text-white/60 text-sm">
                Toggle weekly summaries + clarification digests. These are user-facing emails (not lead routing).
              </p>
            </div>
            <div className="text-white/50 text-xs">
              Preview/edit in <span className="text-white/70 font-semibold">Email Template Studio</span> below.
            </div>
          </div>

          <div className="mt-5">
            {loadingEmailJobs && <div className="text-white/70 text-sm">Loading email job status…</div>}
            {!loadingEmailJobs && emailJobs && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Global toggle */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-bold text-lg">All Email Jobs</div>
                      <div className="text-white/60 text-xs">Daily + Hot Leads + Weekly + Clarifications</div>
                    </div>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={!!emailJobs.all_enabled}
                        onChange={(e) => toggleAllEmailJobs(e.target.checked)}
                      />
                      <div className="w-11 h-6 bg-white/20 rounded-full peer-checked:bg-[var(--pbfs-green)] relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:w-5 after:h-5 after:rounded-full after:transition-transform peer-checked:after:translate-x-5"></div>
                    </label>
                  </div>
                </div>

                {/* Weekly summary toggle */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-bold text-lg">Weekly Summary</div>
                      <div className="text-white/60 text-xs">
                        {(() => {
                          const job = (emailJobs.jobs || []).find((j) => j.id === 'weekly_summary');
                          return job?.next_run_utc ? `Next: ${job.next_run_utc}` : 'Next: —';
                        })()}
                      </div>
                    </div>
                    {(() => {
                      const job = (emailJobs.jobs || []).find((j) => j.id === 'weekly_summary');
                      const enabled = job?.exists ? job.paused === false : false;
                      return (
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!!enabled}
                            onChange={(e) => toggleEmailJob('weekly_summary', e.target.checked)}
                          />
                          <div className="w-11 h-6 bg-white/20 rounded-full peer-checked:bg-[var(--pbfs-green)] relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:w-5 after:h-5 after:rounded-full after:transition-transform peer-checked:after:translate-x-5"></div>
                        </label>
                      );
                    })()}
                  </div>
                </div>

                {/* Clarification digest toggle */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-bold text-lg">Clarification Digest</div>
                      <div className="text-white/60 text-xs">
                        {(() => {
                          const job = (emailJobs.jobs || []).find((j) => j.id === 'pending_clarifications');
                          return job?.next_run_utc ? `Next: ${job.next_run_utc}` : 'Next: —';
                        })()}
                      </div>
                    </div>
                    {(() => {
                      const job = (emailJobs.jobs || []).find((j) => j.id === 'pending_clarifications');
                      const enabled = job?.exists ? job.paused === false : false;
                      return (
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!!enabled}
                            onChange={(e) => toggleEmailJob('pending_clarifications', e.target.checked)}
                          />
                          <div className="w-11 h-6 bg-white/20 rounded-full peer-checked:bg-[var(--pbfs-green)] relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:w-5 after:h-5 after:rounded-full after:transition-transform peer-checked:after:translate-x-5"></div>
                        </label>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
            {!loadingEmailJobs && !emailJobs && (
              <div className="mt-3 text-white/60 text-sm">
                ⚠️ Scheduler not initialized on this environment. On localhost this usually means the backend is missing the
                <span className="text-white font-semibold"> apscheduler</span> dependency.
                <div className="mt-2">
                  <button
                    onClick={loadEmailJobsStatus}
                    className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold border border-white/10"
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Email Template Studio */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold mb-1">Email Template Studio</h2>
              <p className="text-white/60 text-sm">
                Edit copy for any email we send. Values are escaped for safety (no raw HTML).
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={previewSelectedTemplate}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold border border-white/10"
              >
                Preview Selected
              </button>
              <button
                onClick={saveTemplate}
                disabled={savingTemplate}
                className="px-4 py-2 rounded-lg bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 disabled:bg-white/10 disabled:cursor-not-allowed text-white font-semibold border border-white/10"
              >
                {savingTemplate ? 'Saving…' : 'Save Template'}
              </button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm text-white/60 mb-2">Template</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full bg-[#00314F] border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)] focus:ring-2 focus:ring-[var(--pbfs-blue)]/50"
                style={{ colorScheme: 'dark' }}
              >
                {(templates?.length ? templates : [
                  { template_id: 'daily_digest', name: 'Daily Lead Digest' },
                  { template_id: 'hot_lead_digest', name: 'Hot Lead Digest' },
                  { template_id: 'weekly_summary', name: 'Weekly Summary' },
                  { template_id: 'clarification_digest', name: 'Clarification Digest' }
                ]).map((t) => (
                  <option key={t.template_id} value={t.template_id} className="bg-[#00314F] text-white">
                    {t.name || t.template_id}
                  </option>
                ))}
              </select>

              <div className="mt-3 text-xs text-white/50">
                Subjects support placeholders: <span className="text-white/80">{'{lead_count}'}</span>,{' '}
                <span className="text-white/80">{'{date_long}'}</span>, <span className="text-white/80">{'{date_short}'}</span>,{' '}
                <span className="text-white/80">{'{branch_name}'}</span>, <span className="text-white/80">{'{item_count}'}</span>.
              </div>
            </div>

            <div className="lg:col-span-2">
              {loadingTemplate ? (
                <div className="text-white/70 text-sm">Loading template…</div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Subject</label>
                    <input
                      value={templateForm.subject_template}
                      onChange={(e) => setTemplateForm({ ...templateForm, subject_template: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)] focus:ring-2 focus:ring-[var(--pbfs-blue)]/50"
                      placeholder="Subject line"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-white/60 mb-2">Header Title</label>
                      <input
                        value={templateForm.header_title}
                        onChange={(e) => setTemplateForm({ ...templateForm, header_title: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)] focus:ring-2 focus:ring-[var(--pbfs-blue)]/50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/60 mb-2">Header Subtitle</label>
                      <input
                        value={templateForm.header_subtitle}
                        onChange={(e) => setTemplateForm({ ...templateForm, header_subtitle: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)] focus:ring-2 focus:ring-[var(--pbfs-blue)]/50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-white/60 mb-2">Intro Text</label>
                    <textarea
                      value={templateForm.intro_text}
                      onChange={(e) => setTemplateForm({ ...templateForm, intro_text: e.target.value })}
                      rows={3}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)] focus:ring-2 focus:ring-[var(--pbfs-blue)]/50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-white/60 mb-2">Footer Text</label>
                    <input
                      value={templateForm.footer_text}
                      onChange={(e) => setTemplateForm({ ...templateForm, footer_text: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)] focus:ring-2 focus:ring-[var(--pbfs-blue)]/50"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Inbox (Inbound Emails) */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold mb-1">📥 Inbox (Inbound Emails)</h2>
              <p className="text-white/60 text-sm">
                Latest inbound messages captured by the backend (SendGrid inbound parse/webhook). This powers the future AI reply analyzer.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={async () => {
                  try {
                    const analyzeToken = localStorage.getItem('token');
                    const res = await fetch(`${backendUrl}/api/admin/inbound-emails/analyze-latest?limit=10`, {
                      method: 'POST',
                      headers: { 
                        'X-Local-Dev': 'true',
                        ...(analyzeToken && { 'Authorization': `Bearer ${analyzeToken}` })
                      }
                    });
                    const data = await res.json();
                    if (data?.success) {
                      await loadInboundEmails();
                      alert(`✅ Analyzed ${data.count} inbound email(s)`);
                    } else {
                      alert(`❌ Analyze failed: ${data?.detail || data?.error || 'unknown error'}`);
                    }
                  } catch (e) {
                    alert(`❌ Analyze failed: ${e.message}`);
                  }
                }}
                className="px-4 py-2 rounded-lg bg-[var(--pbfs-blue)] hover:bg-[var(--pbfs-blue)]/80 text-white font-semibold border border-white/10"
              >
                Analyze New Replies
              </button>
              <button
                onClick={() => loadInboundEmails()}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold border border-white/10"
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex flex-wrap gap-3 mb-4">
              <select
                value={inboundFilter.status}
                onChange={(e) => {
                  const next = { ...inboundFilter, status: e.target.value };
                  setInboundFilter(next);
                  loadInboundEmails(next);
                }}
                className="bg-[#00314F] border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                style={{ colorScheme: 'dark' }}
              >
                <option value="all" className="bg-[#00314F] text-white">All statuses</option>
                <option value="pending" className="bg-[#00314F] text-white">Pending</option>
                <option value="analyzed" className="bg-[#00314F] text-white">Analyzed</option>
                <option value="failed" className="bg-[#00314F] text-white">Failed</option>
              </select>

              <select
                value={inboundFilter.mapped}
                onChange={(e) => {
                  const next = { ...inboundFilter, mapped: e.target.value };
                  setInboundFilter(next);
                  loadInboundEmails(next);
                }}
                className="bg-[#00314F] border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                style={{ colorScheme: 'dark' }}
              >
                <option value="all" className="bg-[#00314F] text-white">All mapping</option>
                <option value="mapped" className="bg-[#00314F] text-white">Mapped</option>
                <option value="unmapped" className="bg-[#00314F] text-white">Unmapped</option>
              </select>

              <select
                value={inboundFilter.responded}
                onChange={(e) => {
                  const next = { ...inboundFilter, responded: e.target.value };
                  setInboundFilter(next);
                  loadInboundEmails(next);
                }}
                className="bg-[#00314F] border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                style={{ colorScheme: 'dark' }}
              >
                <option value="all" className="bg-[#00314F] text-white">All responses</option>
                <option value="responded" className="bg-[#00314F] text-white">Responded</option>
                <option value="unresponded" className="bg-[#00314F] text-white">Unresponded</option>
              </select>
            </div>

            {loadingInboundEmails ? (
              <div className="text-white/70 text-sm">Loading inbox…</div>
            ) : inboundEmails?.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-white/60 border-b border-white/10">
                      <th className="text-left py-2 pr-4">Received (UTC)</th>
                      <th className="text-left py-2 pr-4">From</th>
                      <th className="text-left py-2 pr-4">Subject</th>
                      <th className="text-left py-2 pr-4">Status</th>
                      <th className="text-left py-2 pr-4">Inspection</th>
                      <th className="text-left py-2 pr-4">Next Move</th>
                      <th className="text-left py-2 pr-4">Actions</th>
                      <th className="text-left py-2">Snippet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inboundEmails.slice(0, 50).map((m, idx) => (
                      <tr key={idx} className="border-b border-white/5">
                        <td className="py-2 pr-4 text-white/80 whitespace-nowrap">{(m.received_at || '').slice(0, 19).replace('T', ' ')}</td>
                        <td className="py-2 pr-4 text-white/80">{m.from || '—'}</td>
                        <td className="py-2 pr-4 text-white/90 font-semibold">{m.subject || '—'}</td>
                        <td className="py-2 pr-4 text-white/70">{m.analysis_status || '—'}</td>
                        <td className="py-2 pr-4 text-white/80 whitespace-nowrap">
                          {m.inspection_id ? (
                            <span className={m.inspection_exists === false ? 'text-red-300' : 'text-white/80'}>
                              {String(m.inspection_id).slice(-8)}
                              {m.inspection_exists === false ? ' (missing)' : ''}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="py-2 pr-4 text-white/70">{m.analysis?.recommended_next_action || '—'}</td>
                        <td className="py-2 pr-4">
                          {m.inspection_id ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => takeReplyAction({
                                  inspectionId: m.inspection_id,
                                  inboundEmailId: m.id,
                                  action: 'mark_resolved',
                                  notes: `Resolved from inbound email ${m.id || ''}`
                                })}
                                disabled={m.inspection_exists === false}
                                className="px-3 py-1 rounded-lg bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 text-white font-semibold text-xs"
                              >
                                Mark Resolved
                              </button>
                              <button
                                onClick={() => {
                                  const notes = window.prompt('What do you want to ask for / note for admin?') || '';
                                  takeReplyAction({
                                    inspectionId: m.inspection_id,
                                    inboundEmailId: m.id,
                                    action: 'request_more_info',
                                    notes
                                  });
                                }}
                                disabled={m.inspection_exists === false}
                                className="px-3 py-1 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold text-xs border border-white/10"
                              >
                                Request More Info
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedEmailForReply(m);
                                  setReplyModalOpen(true);
                                }}
                                className="px-3 py-1 rounded-lg bg-[var(--pbfs-blue)] hover:bg-[var(--pbfs-blue)]/80 text-white font-semibold text-xs"
                              >
                                Reply
                              </button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm('Delete this inbound email? This cannot be undone.')) return;
                                  try {
                                    const deleteToken = localStorage.getItem('token');
                                    const res = await fetch(`${backendUrl}/api/admin/inbound-emails/${encodeURIComponent(m.id)}`, {
                                      method: 'DELETE',
                                      headers: { 
                                        'X-Local-Dev': 'true',
                                        ...(deleteToken && { 'Authorization': `Bearer ${deleteToken}` })
                                      }
                                    });
                                    const data = await res.json();
                                    if (!data?.success) throw new Error(data?.detail || data?.error || 'Failed');
                                    await loadInboundEmails();
                                  } catch (e) {
                                    alert(`❌ Delete failed: ${e.message}`);
                                  }
                                }}
                                className="px-3 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-400 font-semibold text-xs border border-red-500/30"
                              >
                                Delete
                              </button>
                            </div>
                          ) : (
                            <span className="text-white/40 text-xs">—</span>
                          )}
                        </td>
                        <td className="py-2 text-white/70">
                          {(() => {
                            // Safely extract text from email (CodeQL: fix incomplete multi-character sanitization)
                            const textContent = m.text || '';
                            const htmlContent = m.html || '';
                            
                            if (textContent.trim()) {
                              return textContent.trim().slice(0, 120);
                            }
                            
                            // For HTML, use DOMParser for safe text extraction
                            if (htmlContent) {
                              try {
                                const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
                                return (doc.body.textContent || '').trim().slice(0, 120) || '—';
                              } catch {
                                // Fallback: multiple passes to ensure complete tag removal
                                let cleaned = htmlContent;
                                let prevLength = -1;
                                while (cleaned.length !== prevLength) {
                                  prevLength = cleaned.length;
                                  cleaned = cleaned.replace(/<[^>]*>/g, '');
                                }
                                return cleaned.trim().slice(0, 120) || '—';
                              }
                            }
                            
                            return '—';
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-white/60 text-sm">No inbound emails captured yet.</div>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-500/20 border border-red-500 rounded-lg p-4">
            <p className="text-red-400">❌ {error}</p>
          </div>
        )}

        {/* Controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Preview Section */}
          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4">Preview Digest</h2>
            <p className="text-white/60 mb-4">
              Generate a preview of the email digest before sending
            </p>

            <div className="mb-4">
              <label className="block text-sm text-white/60 mb-2">Time Window (hours)</label>
              <select
                value={hoursBack}
                onChange={(e) => setHoursBack(parseInt(e.target.value))}
                className="w-full bg-[#00314F] border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)] focus:ring-2 focus:ring-[var(--pbfs-blue)]/50"
                style={{ colorScheme: 'dark' }}
              >
                <option value={1} className="bg-[#00314F] text-white">Last 1 hour</option>
                <option value={6} className="bg-[#00314F] text-white">Last 6 hours</option>
                <option value={24} className="bg-[#00314F] text-white">Last 24 hours (daily)</option>
                <option value={168} className="bg-[#00314F] text-white">Last 7 days (weekly)</option>
              </select>
            </div>

            <button
              onClick={handlePreview}
              disabled={loading}
              className="w-full bg-[var(--pbfs-blue)] hover:bg-[var(--pbfs-blue)]/80 disabled:bg-white/10 disabled:cursor-not-allowed py-3 rounded-lg font-semibold transition-colors"
            >
              {loading ? 'Loading...' : 'Generate Preview'}
            </button>
          </div>

          {/* Send Section */}
          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4">Send Digest</h2>
            <p className="text-white/60 mb-6">
              Manually trigger the daily email digest to all recipients
            </p>
            <p className="text-sm text-white/40 mb-4">
              Test Recipients: Houston AI Team (Matt, Shiva, Mohith)
            </p>
            <button
              onClick={handleSendDigest}
              disabled={sending}
              className="w-full bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 disabled:bg-white/10 disabled:cursor-not-allowed py-3 rounded-lg font-semibold transition-colors"
            >
              {sending ? 'Sending...' : 'Send Now'}
            </button>
          </div>

          {/* Hot Lead Digest Section */}
          <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4">Hot Lead Digest</h2>
            <p className="text-white/60 mb-6">
              Send alerts for inspections that are DUE or PAST DUE
            </p>
            <p className="text-sm text-white/40 mb-4">
              Test Recipients: Houston AI Team<br />
              Finds inspections where next_due_date ≤ today
            </p>
            <div className="space-y-3">
              <button
                onClick={handleSendHotLeadDigest}
                disabled={sending}
                className="w-full bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 disabled:bg-white/10 disabled:cursor-not-allowed py-3 rounded-lg font-semibold transition-colors"
              >
                {sending ? 'Sending...' : 'Send Hot Leads'}
              </button>

              {/* Professional Test Email Button */}
              <button
                onClick={async () => {
                  setSending(true);
                  try {
                    const proToken = localStorage.getItem('token');
                    const response = await fetch(`${backendUrl}/api/admin/send-test-email-professional`, {
                      method: 'POST',
                      headers: {
                        ...(proToken && { 'Authorization': `Bearer ${proToken}` })
                      }
                    });
                    const data = await response.json();
                    if (data.success) {
                      alert(`✅ Professional test email sent!\n\nRecipients: ${data.recipients.join(', ')}\n\nCheck your inbox to see the clean, professional format!`);
                    } else {
                      alert(`❌ Failed: ${data.error}`);
                    }
                  } catch (error) {
                    alert(`❌ Error: ${error.message}`);
                  } finally {
                    setSending(false);
                  }
                }}
                disabled={sending}
                className="w-full bg-[var(--pbfs-blue)] hover:bg-[var(--pbfs-blue)]/80 disabled:bg-white/10 disabled:cursor-not-allowed py-3 rounded-lg font-semibold transition-colors text-sm"
              >
                📧 Send Professional Test Email
              </button>
            </div>
          </div>

        </div>

        {/* Preview Display */}
        {previewData && (
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-8">
            <h2 className="text-2xl font-bold mb-4">📬 Email Preview</h2>

            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm">Subject</label>
                <div className="bg-gray-900 p-3 rounded mt-1 font-semibold">
                  {previewData.subject}
                </div>
              </div>

              <div>
                <label className="text-gray-400 text-sm">Recipients ({previewData.recipients.length})</label>
                <div className="bg-gray-900 p-3 rounded mt-1 text-sm">
                  {previewData.recipients.join(', ')}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-900 p-4 rounded">
                  <p className="text-gray-400 text-sm">Total Leads</p>
                  <p className="text-3xl font-bold text-blue-400">{previewData.inspectionCount}</p>
                </div>
                <div className="bg-gray-900 p-4 rounded">
                  <p className="text-gray-400 text-sm">Unassigned</p>
                  <p className="text-3xl font-bold text-yellow-400">{previewData.pendingCount}</p>
                </div>
                <div className="bg-gray-900 p-4 rounded">
                  <p className="text-gray-400 text-sm">Unique Businesses</p>
                  <p className="text-3xl font-bold text-green-400">{previewData.newBusinesses.length}</p>
                </div>
              </div>

              {/* Leads by Branch */}
              {previewData.branches && previewData.branches.length > 0 && (
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Leads by Branch</label>
                  <div className="space-y-3">
                    {previewData.branches.map((branch, index) => {
                      const leads = previewData.leadsByBranch[branch] || [];
                      return (
                        <div key={index} className="bg-gray-900 p-4 rounded">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-blue-400">📍 {branch}</h3>
                            <span className="text-sm text-gray-500">{leads.length} lead(s)</span>
                          </div>
                          <ul className="text-sm text-gray-300 space-y-1">
                            {leads.slice(0, 5).map((lead, i) => (
                              <li key={i} className="pl-4">
                                • {lead.business_name} <span className="text-gray-500">({lead.submitted_by})</span>
                              </li>
                            ))}
                            {leads.length > 5 && (
                              <li className="pl-4 text-gray-500 italic">+ {leads.length - 5} more...</li>
                            )}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Unassigned leads warning */}
              {previewData.pendingCount > 0 && (
                <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-4">
                  <h3 className="font-semibold text-yellow-400 mb-2">⚠️ Unassigned Locations</h3>
                  <p className="text-sm text-gray-300">
                    {previewData.pendingCount} lead(s) need branch assignment
                  </p>
                </div>
              )}

              <div>
                <label className="text-gray-400 text-sm">All Business Names</label>
                <div className="bg-gray-900 p-3 rounded mt-1 max-h-40 overflow-y-auto">
                  {previewData.newBusinesses.length > 0 ? (
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      {previewData.newBusinesses.map((business, index) => (
                        <li key={index}>{business}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-sm">No business names found in this time period</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scheduler Configuration */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4">⏰ Schedule Configuration</h2>

          {schedulerStatus ? (
            <div className="bg-green-500/20 border border-green-500/40 rounded-lg p-4 mb-4">
              <p className="font-semibold mb-2 text-green-300">✅ Scheduler Active</p>
              {schedulerStatus.jobs && schedulerStatus.jobs.find(j => j.id === 'daily_digest') && (
                <div className="text-sm text-white/80">
                  <span className="font-semibold">Next Send:</span> {new Date(schedulerStatus.jobs.find(j => j.id === 'daily_digest').next_run_utc).toLocaleString('en-US', { timeZone: 'America/Chicago' })}
                </div>
              )}
              {sendGridConnected && (
                <p className="text-green-300 text-xs mt-2">✅ SendGrid connected</p>
              )}
            </div>
          ) : (
            <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-4 mb-4">
              <p className="font-semibold text-red-300">❌ Scheduler Not Running</p>
              <p className="text-white/60 text-sm mt-1">Check Azure logs for errors</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-white/60 text-sm mb-2">Schedule Type</label>
              <select
                value={scheduleType}
                onChange={(e) => setScheduleType(e.target.value)}
                className="w-full bg-[#00314F] border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)]"
                style={{ colorScheme: 'dark' }}
              >
                <option value="daily" className="bg-[#00314F] text-white">Daily (recurring)</option>
                <option value="once" className="bg-[#00314F] text-white">One-time send</option>
                <option value="disabled" className="bg-[#00314F] text-white">Disabled (manual only)</option>
              </select>
            </div>

            {scheduleType === 'once' && (
              <div>
                <label className="block text-white/60 text-sm mb-2">Date</label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)]"
                />
              </div>
            )}

            {scheduleType !== 'disabled' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/60 text-sm mb-2">Hour (CST)</label>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={scheduleHour}
                    onChange={(e) => setScheduleHour(parseInt(e.target.value))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)]"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-2">Minute</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={scheduleMinute}
                    onChange={(e) => setScheduleMinute(parseInt(e.target.value))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[var(--pbfs-blue)]"
                  />
                </div>
              </div>
            )}

            <button
              onClick={handleUpdateSchedule}
              disabled={!schedulerStatus}
              className="bg-[var(--pbfs-blue)] hover:bg-[var(--pbfs-blue)]/80 disabled:bg-white/10 px-6 py-2 rounded-lg font-semibold transition-colors"
            >
              {scheduleType === 'disabled' ? 'Disable Scheduler' : 'Update Schedule'}
            </button>

            <p className="text-white/40 text-xs">
              {scheduleType === 'daily' && `Will send every day at ${scheduleHour}:${String(scheduleMinute).padStart(2, '0')} CST`}
              {scheduleType === 'once' && scheduleDate && `Will send once on ${scheduleDate} at ${scheduleHour}:${String(scheduleMinute).padStart(2, '0')} CST`}
              {scheduleType === 'disabled' && 'Automated emails disabled - manual trigger only'}
            </p>
          </div>
        </div>

        {/* Recipient Configuration */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold">📧 Email Recipients</h2>
              <button
                onClick={() => setShowRecipients(!showRecipients)}
                className="text-white/60 hover:text-white text-sm px-2"
              >
                {showRecipients ? '▼' : '▶'} {regionalOfficers.length}
              </button>
            </div>
            <a
              href="/admin/regional-officers"
              className="text-[var(--pbfs-blue)] hover:underline text-sm"
            >
              Manage →
            </a>
          </div>

          {showRecipients && (
            loadingOfficers ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--pbfs-green)] mx-auto"></div>
                <p className="text-white/60 text-sm mt-2">Loading...</p>
              </div>
            ) : regionalOfficers.length > 0 ? (
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                  {regionalOfficers.map(officer => (
                    <a
                      key={officer.id}
                      href="/admin/regional-officers"
                      className="bg-white/5 border border-white/10 hover:border-[var(--pbfs-blue)] rounded px-3 py-2 transition-colors block"
                    >
                      <p className="text-white font-semibold">{officer.name}</p>
                      <p className="text-white/60 text-sm">{officer.email}</p>
                      <div className="flex justify-between items-center mt-1">
                        <p className="text-white/40 text-xs">{officer.region}</p>
                        <p className="text-white/40 text-xs">{officer.states?.join(', ')}</p>
                      </div>
                    </a>
                  ))}
                </div>
                <p className="text-white/40 text-xs mt-3">
                  Click any officer to edit in Regional Officers page
                </p>
              </div>
            ) : (
              <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-lg p-4">
                <p className="text-yellow-300 font-semibold mb-2">⚠️ No Recipients</p>
                <a
                  href="/admin/regional-officers"
                  className="inline-block bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 px-4 py-2 rounded text-sm font-semibold transition-colors"
                >
                  Add Regional Officers
                </a>
              </div>
            )
          )}

          <div className="bg-white/5 border border-white/10 rounded-lg p-3 mt-4">
            <p className="text-white/60 text-sm">🔔 Fallback: <span className="text-white/80 font-mono">matt.hales@pyebarkerfs.com</span></p>
          </div>
        </div>

        {/* Email Activity Log - GitHub Actions Style */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold">📊 Email Activity</h2>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-white/60 hover:text-white text-sm px-2"
              >
                {showHistory ? '▼' : '▶'} {emailHistory.length}
              </button>
            </div>
            <button
              onClick={loadEmailHistory}
              className="text-white/60 hover:text-white text-sm"
            >
              🔄 Refresh
            </button>
          </div>

          {showHistory && (
            loadingHistory ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--pbfs-green)] mx-auto"></div>
                <p className="text-white/60 text-sm mt-2">Loading activity...</p>
              </div>
            ) : emailHistory.length > 0 ? (
              <div className="bg-black/30 border border-white/10 rounded-lg p-4 max-h-96 overflow-y-auto">
                <div className="space-y-3">
                  {emailHistory.map((event, idx) => (
                    <div key={idx} className="border-l-2 border-white/20 pl-4 py-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`w-2 h-2 rounded-full ${event.success ? 'bg-green-400' : 'bg-red-400'}`}></span>
                            <span className="text-white font-semibold">{event.type === 'daily' ? '📧 Daily Digest' : '🔥 Hot Leads'}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${event.success ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                              {event.success ? '✓ Sent' : '✗ Failed'}
                            </span>
                          </div>
                          <div className="text-sm text-white/70 space-y-1">
                            <p>• Leads: {event.leads_count}</p>
                            <p>• Recipients: {event.recipients?.join(', ') || 'Unknown'}</p>
                            {event.error && <p className="text-red-300">• Error: {event.error}</p>}
                          </div>
                        </div>
                        <div className="text-right text-xs text-white/40">
                          {new Date(event.timestamp).toLocaleString('en-US', { timeZone: 'America/Chicago' })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-white/40">
                <p>No email activity yet</p>
                <p className="text-xs mt-2">Send a digest to see activity here</p>
              </div>
            )
          )}
        </div>

        {/* How Email Routing Works (collapsed) */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6">
          <button
            onClick={() => setShowRoutingHelp(!showRoutingHelp)}
            className="w-full flex items-center justify-between"
          >
            <h2 className="text-2xl font-bold">📮 How Email Routing Works</h2>
            <span className="text-white/60 font-semibold">{showRoutingHelp ? 'Hide' : 'Show'}</span>
          </button>

          {showRoutingHelp && (
            <div className="mt-5 space-y-6">
              {/* Sales Lead Flow */}
              <div>
                <h3 className="text-lg font-bold text-[var(--pbfs-green)] mb-3">📊 Sales Lead Emails (to Regional Officers)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="font-semibold text-white mb-1">Daily Digest • 8:30 AM CST</p>
                    <p className="text-white/60 text-xs">Batches all approved leads from the past 24 hours and sends to regional sales officers based on location.</p>
                    <p className="text-white/40 text-xs mt-2">From: <span className="font-mono">leads@pyebarkerfs.com</span></p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="font-semibold text-white mb-1">Hot Lead Alerts • Every Hour</p>
                    <p className="text-white/60 text-xs">Sends urgent alerts for leads with past-due or upcoming inspection dates.</p>
                    <p className="text-white/40 text-xs mt-2">From: <span className="font-mono">leads@pyebarkerfs.com</span></p>
                  </div>
                </div>
              </div>

              {/* User-Facing Emails */}
              <div>
                <h3 className="text-lg font-bold text-[var(--pbfs-blue)] mb-3">👤 User Emails (to Field Technicians)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="font-semibold text-white mb-1">Weekly Summary • Monday 7 AM</p>
                    <p className="text-white/60 text-xs">Personalized stats email showing each user their scans, points, and ranking for the past week.</p>
                    <p className="text-white/40 text-xs mt-2">From: <span className="font-mono">tagreader@pyebarkerfs.com</span></p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="font-semibold text-white mb-1">Clarification Digest • Every :15</p>
                    <p className="text-white/60 text-xs">Reminds users about leads that need more information (missing address, date, etc).</p>
                    <p className="text-white/40 text-xs mt-2">From: <span className="font-mono">tagquest@pyebarkerfs.com</span></p>
                  </div>
                </div>
              </div>

              {/* Inbound Reply System */}
              <div>
                <h3 className="text-lg font-bold text-yellow-400 mb-3">📥 Inbound Reply System (AI-Powered)</h3>
                <div className="bg-white/5 rounded-lg p-4 text-sm">
                  <div className="space-y-2">
                    <p className="text-white/80"><span className="font-semibold text-white">1.</span> User receives clarification email with reply code (e.g., TQ-ORMQQ55BHVG4E)</p>
                    <p className="text-white/80"><span className="font-semibold text-white">2.</span> User replies to <span className="font-mono bg-white/10 px-1 rounded">TagQuest@pyebarkerfs.com</span></p>
                    <p className="text-white/80"><span className="font-semibold text-white">3.</span> SendGrid Inbound Parse routes reply to backend webhook</p>
                    <p className="text-white/80"><span className="font-semibold text-white">4.</span> AI (Gemini) analyzes reply, extracts key facts, recommends next action</p>
                    <p className="text-white/80"><span className="font-semibold text-white">5.</span> Admin sees reply in Inbox above with "Mark Resolved", "Request More Info", or "Reply" options</p>
                  </div>
                </div>
              </div>

              {/* Fallback */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-1 text-blue-300">🔔 Fallback Routing</h3>
                <p className="text-white/70 text-xs">
                  If no regional officer is assigned for a region, leads are sent to:
                  <span className="font-mono bg-white/10 px-2 py-0.5 rounded ml-2 text-white">matt.hales@pyebarkerfs.com</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Template Preview Modal */}
      {showTemplatePreview && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-[var(--pbfs-navy)] rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-white/10 shadow-2xl">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="text-white font-bold">{templatePreviewTitle}</div>
              <button
                onClick={() => setShowTemplatePreview(false)}
                className="text-white/70 hover:text-white text-2xl leading-none"
                title="Close"
              >
                ×
              </button>
            </div>
            <div className="bg-white">
              <iframe
                title="Email Template Preview"
                srcDoc={templatePreviewHtml || '<p>Loading…</p>'}
                style={{ width: '100%', height: '75vh', border: '0' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Email Reply Modal */}
      <EmailReplyModal
        isOpen={replyModalOpen}
        onClose={() => {
          setReplyModalOpen(false);
          setSelectedEmailForReply(null);
        }}
        email={selectedEmailForReply}
        backendUrl={backendUrl}
        onEmailSent={() => {
          loadInboundEmails();
        }}
      />
    </div>
  );
};

export default EmailDigestManagement;



